import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import Column from "@amzn/meridian/column";
import Table, { TableRow, TableCell } from "@amzn/meridian/table"
import { TableSortDirection } from "@amzn/meridian/table/table";
import Input from "@amzn/meridian/input";
import Pagination from "@amzn/meridian/pagination"
import Row from "@amzn/meridian/row";
import Select, { SelectOption } from "@amzn/meridian/select"
import Text from "@amzn/meridian/text";
import GreenCheckMarkWithTooltip from "./GreenCheckMarkWithTooltip";
import RedCrossWithTooltip from "./RedCrossWithTooltip";
import AssociateAliasCell from "./AssociateAliasCell";
import TrainedRolesCell from "./TrainedRolesCell";
import {
  AUDIT_TABLE_OBJECT_LIST,
  ASSOCIATE_ALIAS_FIELDNAME,
  DEFAULT_ITEMS_PER_PAGE,
  TRAINED_ROLES_FIELDNAME,
  BADGE_IN_STATUS_FIELDNAME,
  LABOR_TRACKED_FIELDNAME,
  TIMESTAMP_FIELDNAME,
  ASSIGNED_ROLE_FIELDNAME,
  DROPDOWN_TYPE,
} from "../../constants";
import "./AuditComponents.scss";
import orderBy from "lodash/orderBy";
import SearchableSelect from "./SearchableSelect";
import uniq from "lodash/uniq";
import debounce from "lodash/debounce";

const BADGE_STATUS_TOKEN_MAP = {
  "Scanned": <><GreenCheckMarkWithTooltip />{" "}Scanned</>,
  "Not Scanned": <><RedCrossWithTooltip />{" "}Not Scanned</>,
}

export default function AuditDetailsTable({ auditRecordList }) {
  const { nodeId } = useParams<{ nodeId: string }>();
  const [userFilters, setUserFilters] = useState({});
  const [debouncedFilters, setDebouncedFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [sortColumn, setSortColumn] = useState(TIMESTAMP_FIELDNAME);
  const [sortDirection, setSortDirection] = useState<TableSortDirection>("ascending");

  // Debounce filter updates to reduce computation
  const debouncedSetFilters = useMemo(
      () => debounce((filters) => setDebouncedFilters(filters), 300),
      []
  );

  useEffect(() => {
    debouncedSetFilters(userFilters);
  }, [userFilters, debouncedSetFilters]);

  // Memoize dropdown options to prevent recalculation
  const dropdownOptionsCache = useMemo(() => {
    const cache = {};
    AUDIT_TABLE_OBJECT_LIST.forEach(({ fieldName, fieldType }) => {
      if (fieldType === DROPDOWN_TYPE) {
        if (fieldName === TRAINED_ROLES_FIELDNAME) {
          const fieldValueOptions = auditRecordList
              .filter(record => record[fieldName] !== null)
              .flatMap(record => record[fieldName]);
          cache[fieldName] = uniq(fieldValueOptions);
        } else if (fieldName === LABOR_TRACKED_FIELDNAME) {
          cache[fieldName] = ["Yes", "No"];
        } else {
          const fieldValueOptions = auditRecordList.map(record => record[fieldName]);
          cache[fieldName] = uniq(fieldValueOptions);
        }
      }
    });
    return cache;
  }, [auditRecordList]);

  // Optimized filter method with early returns
  const filterMethod = useCallback((userAuditRecord) => {
    for (const key in debouncedFilters) {
      const filterValue = debouncedFilters[key];
      if (!filterValue) continue;

      const recordValue = userAuditRecord[key];
      if (recordValue === undefined || recordValue === null) {
        // For laborTracked, null/undefined should be treated as "No"
        if (key === LABOR_TRACKED_FIELDNAME) {
          const expectedValue = filterValue.toLowerCase() === "yes";
          if (expectedValue) return false; // Looking for "Yes" but record is null/undefined
        } else {
          return false;
        }
        continue;
      }

      if (Array.isArray(recordValue)) {
        // trainedRoles - use some() for early termination
        if (!recordValue.some(role =>
            role.toString().toLowerCase().includes(filterValue.toLowerCase())
        )) return false;
      } else if (key === LABOR_TRACKED_FIELDNAME) {
        // Handle laborTracked specifically - could be boolean, string, or other types
        const expectedValue = filterValue.toLowerCase() === "yes";
        let actualValue = false;

        if (typeof recordValue === 'boolean') {
          actualValue = recordValue;
        } else if (typeof recordValue === 'string') {
          actualValue = recordValue.toLowerCase() === 'true' || recordValue.toLowerCase() === 'yes';
        } else {
          actualValue = Boolean(recordValue);
        }

        if (actualValue !== expectedValue) return false;
      } else if (key === BADGE_IN_STATUS_FIELDNAME) {
        if (!recordValue.toLowerCase().startsWith(filterValue.toLowerCase())) return false;
      } else {
        // Handle string fields (AssociateAlias, AssignedRole, etc.) with partial matching
        const stringValue = recordValue?.toString() || "";
        if (!stringValue.toLowerCase().includes(filterValue.toLowerCase())) return false;
      }
    }
    return true;
  }, [debouncedFilters]);

  // Memoize filtered and sorted data
  const processedData = useMemo(() => {
    let filtered = auditRecordList.filter(filterMethod);

    if (sortColumn) {
      filtered = orderBy(filtered, [sortColumn], [sortDirection === "ascending" ? "asc" : "desc"]);
    }

    return filtered;
  }, [auditRecordList, filterMethod, sortColumn, sortDirection]);

  // Pagination calculations
  const firstVisibleIndex = (currentPage - 1) * itemsPerPage;
  const lastVisibleIndex = Math.min(firstVisibleIndex + itemsPerPage, processedData.length);
  const numberOfPages = Math.ceil(processedData.length / itemsPerPage);
  const visibleData = processedData.slice(firstVisibleIndex, lastVisibleIndex);

  const formatTimestampCell = useCallback((timestampStr) => {
    if (!timestampStr || !timestampStr.includes("T") || !timestampStr.includes("Z")) {
      return "";
    }
    return timestampStr.split("T")[1].split("Z")[0].split(".")[0];
  }, []);

  const getTableCellValueFromFieldName = useCallback((auditRecord, fieldName) => {
    switch (fieldName) {
      case TIMESTAMP_FIELDNAME:
        return formatTimestampCell(auditRecord[fieldName]);
      case ASSOCIATE_ALIAS_FIELDNAME:
        const ppaUrl = `https://fclm-portal.amazon.com/employee/ppaTimeDetails?employeeId=${auditRecord[fieldName]}&warehouseId=${nodeId}`;
        return <AssociateAliasCell ppaUrl={ppaUrl} associateAlias={auditRecord[fieldName]} />;
      case TRAINED_ROLES_FIELDNAME:
        const trainedRolesStr = auditRecord[fieldName]?.join(",") || "";
        return <TrainedRolesCell trainedRolesStr={trainedRolesStr} />;
      case BADGE_IN_STATUS_FIELDNAME:
        return BADGE_STATUS_TOKEN_MAP[auditRecord[fieldName]];
      case LABOR_TRACKED_FIELDNAME:
        return auditRecord[fieldName] ? "Yes" : "No";
      default:
        return auditRecord[fieldName];
    }
  }, [nodeId, formatTimestampCell]);

  const changeUserFiltersHandler = useCallback((fieldName) => (inputValue) => {
    setUserFilters(prev => ({
      ...prev,
      [fieldName]: inputValue
    }));
    setCurrentPage(1);
  }, []);

  const sortAuditRecordsHandler = useCallback((event) => {
    setSortColumn(event.sortColumn);
    setSortDirection(event.sortDirection);
  }, []);

  const getInputFilter = useCallback((title, fieldName, fieldType) => {
    if (!auditRecordList.length) return null;

    if (fieldType === DROPDOWN_TYPE) {
      return (
          <SearchableSelect
              userSelectValue={userFilters[fieldName]}
              changeValueHandler={changeUserFiltersHandler}
              fieldName={fieldName}
              title={title}
              options={dropdownOptionsCache[fieldName] || []}
          />
      );
    }

    return (
        <Input
            key={`${fieldName}-filter`}
            placeholder={title}
            value={userFilters[fieldName] || ""}
            onChange={changeUserFiltersHandler(fieldName)}
        />
    );
  }, [auditRecordList.length, userFilters, changeUserFiltersHandler, dropdownOptionsCache]);

  return (
      <Column>
        <Table
            headerRows={1}
            stickyHeaderRow
            showDividers
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={sortAuditRecordsHandler}
            layout="fixed"
        >
          <TableRow>
            {AUDIT_TABLE_OBJECT_LIST.map(({ title, fieldName }) => (
                <TableCell key={title} sortColumn={fieldName}>{title}</TableCell>
            ))}
          </TableRow>
          <TableRow>
            {AUDIT_TABLE_OBJECT_LIST.map(({ title, fieldName, fieldType }) => (
                <TableCell key={`${title}-input`}>
                  {getInputFilter(title, fieldName, fieldType)}
                </TableCell>
            ))}
          </TableRow>
          {visibleData.map((auditRecord, index) => {
            const { assignedRole, associateLaborTrackType, associateAlias, timestamp } = auditRecord;
            return (
                <TableRow key={`${associateAlias}-${associateLaborTrackType}-${assignedRole}-${timestamp || index}-row`}>
                  {AUDIT_TABLE_OBJECT_LIST.map(({ fieldName }) => (
                      <TableCell key={`${associateAlias}-${associateLaborTrackType}-${timestamp || index}-${fieldName}-cell`}>
                        {getTableCellValueFromFieldName(auditRecord, fieldName)}
                      </TableCell>
                  ))}
                </TableRow>
            );
          })}
        </Table>
        <Row alignmentHorizontal="justify">
          <Row>
            <Text>{`Showing ${firstVisibleIndex + 1} to ${lastVisibleIndex} of ${processedData.length} entries`}</Text>
            <Select value={itemsPerPage} onChange={setItemsPerPage} size="small">
              <SelectOption value={10} label="10" />
              <SelectOption value={20} label="20" />
              <SelectOption value={30} label="30" />
              <SelectOption value={40} label="40" />
            </Select>
            <Text>per page</Text>
          </Row>
          <Pagination
              showSkipArrows={true}
              numberOfPages={numberOfPages}
              onChange={setCurrentPage}
              currentPage={currentPage}
          />
        </Row>
      </Column>
  );
}